import { useAuth } from '@/lib/auth';
import './EmailLoginForm.scss';
import { ReactComponent as Logo } from '@/assets/img/p-logo-r.svg';
import { ReactComponent as PasswordShowIcon } from '@/assets/img/password-show-icon.svg';
import { ReactComponent as BackIcon } from '@/assets/img/pet-back-arrow.svg';
import { ReactComponent as PasswordHideIcon } from '@/assets/img/password-hide-icon.svg';
import { ChangeEvent, useEffect, useState } from 'react';
import { showNotification } from '@/utils/notificationUtils';
import Utils from '@/utils/util';
import PhoneInput from 'react-phone-input-2';
import { Loader } from '@/components/Loader/Loader';
import { useNavigate } from 'react-router-dom';
import { forgotPasswordandReset } from '../api/forgotPassword';
import { useTranslation } from 'react-i18next';

import { API_URL, ENABLE_GOOGLE_CONVERSION, SUPER_ADMIN_ROLE } from '@/config';
import OTPInput, { ResendOTP } from 'otp-input-react';

import { Button } from '@/components/Elements';
import { useOTPLogin } from '../api/otpLogin';
import { OTPSignupFlow } from './OtpSignupFlow';
import storage from '@/utils/storage';
type UserData = {
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
};

type LoginFormProps = {
  onBack: () => void;
  onLoginSuccess: (data: any) => void;
};

export const EmailLoginForm = ({ onBack, onLoginSuccess }: LoginFormProps) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSignUp, setIsSignup] = useState<boolean>(false);
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const [loginType, setLoginType] = useState('password');
  const useOTPLoginMutation = useOTPLogin();
  const [otpScreenData, setEnterOtpScreen] = useState<any>(null);
  const [otp, setOtp] = useState('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.id === 'customer-mail') {
      setEmail(event.target.value);
    }
  };
  const { t, i18n } = useTranslation();

  const [userData, setUserData] = useState<UserData>({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  useEffect(() => {
    Utils.scrollToTop();
  }, []);

  const handleFormChange = (formFieldName: string, value: string) => {
    userData[formFieldName as keyof typeof userData] = value;
    setUserData({ ...userData });
  };

  const extractUrlParams = (extend = false) => {
    const urlParams = new URLSearchParams(window.location.search);
    const params: any = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    if (extend) {
      params.app_url = window.location.href;
      params.referrer_url = document.referrer;
    }
    return params;
  };

  const validate = () => {
    const { email, password, confirmPassword, phone, firstName, lastName } = userData;
    if (!email || email.trim().length < 1 || !Utils.isValidEmail(email)) {
      showNotification({
        type: 'info',
        title: t('Please enter a valid email'),
      });
      return false;
    }

    if (!password && loginType === 'password') {
      showNotification({
        type: 'info',
        title: t('Please enter a password'),
      });
      return false;
    }

    if (isSignUp) {
      if (password && password.length < 8) {
        showNotification({
          type: 'info',
          title: t('Password length should be at least 8 characters.'),
        });
        return false;
      }
      if (!confirmPassword || password !== confirmPassword) {
        showNotification({
          type: 'info',
          title: t('Passwords do not match'),
        });
        return false;
      }

      // if (!phone || phone.trim().length < 1) {
      //     showNotification({
      //         type: 'info',
      //         title: 'Please enter a valid phone number',
      //     });
      //     return false;
      // }

      if (!firstName || firstName.trim().length < 1) {
        showNotification({
          type: 'info',
          title: t('Please enter a first name'),
        });
        return false;
      }
      if (!lastName || lastName.trim().length < 1) {
        showNotification({
          type: 'info',
          title: t('Please enter a last name'),
        });
        return false;
      }
    }

    return true;
  };
  const onRegister = async () => {
    const { email, password, confirmPassword, phone, firstName, lastName } = userData;
    if (validate()) {
      setIsLoading(true);
      if (isSignUp) {
        try {
          const res = await register({
            email,
            password,
            confirmPassword,
            firstName,
            lastName,
          });
          if (res) {
            const windowRef: any = window;
            if (res.isFirstTimeSignup && ENABLE_GOOGLE_CONVERSION === 'yes' && windowRef?.fbq) {
              console.log('pixels getting tracked for Signup ->');
              windowRef?.fbq('track', 'Signup', {
                email: res.email,
                subscriptionType:
                  res?.subscriptionDetails?.productName === 'Petsked Premium' ? 'Paid' : 'Free',
              });
              //windowRef?.fbq('track', 'Subscribe', { contents: [{ email: res.email, subscriptionType: res?.subscriptionDetails?.productName === 'Petsked Premium' ? 'Paid' : 'Free' }] });
            }
            const urlParams = extractUrlParams();
            if (res.isFirstTimeSignup && windowRef.gtag && ENABLE_GOOGLE_CONVERSION === 'yes') {
              const options = { user_email: res.email, ...urlParams };
              windowRef.gtag('event', 'conversion', {
                send_to: 'AW-16550157434/TmK6CLnhwq8ZEPq43dM9',
              });
              windowRef.gtag('event', 'petsked_signup', {
                user_email: res.email,
                user_utm_campaign: options.utm_campaign,
                user_utm_source: options.utm_source,
                user_utm_medium: options.utm_medium,
                user_referrer: options.referrer,
              });
            }
            urlParams.promoCode && storage.setKey(`promoCode`, urlParams.promoCode);
            setIsLoading(false);
          } else {
            setIsLoading(false);
          }
        } catch (error) {
          setIsLoading(false);
        }
      } else {
        try {
          if (loginType === 'otp') {
            const res = await useOTPLoginMutation.mutateAsync({ email });
            if (res) {
              setEnterOtpScreen(res);
              setIsLoading(false);
              showNotification({
                type: 'success',
                title: 'Otp sent successfully on your email',
              });
            } else {
              showNotification({
                type: 'error',
                title: 'Invalid email.',
              });
            }
          } else {
            const res = await login({
              email,
              password,
              type: loginType,
            });
            const urlParams = extractUrlParams();
            urlParams.promoCode && storage.setKey(`promoCode`, urlParams.promoCode);
            if (res) {
              setIsLoading(false);
            } else {
              setIsLoading(false);
            }
          }
        } catch (error) {
          setIsLoading(false);
        }
      }
    }
  };

  const onVerifyOtp = async () => {
    const { email } = userData;

    if (!otp || otp.trim().length < 6) {
      showNotification({
        type: 'info',
        title: 'Please enter 6 digit code',
      });
      return false;
    } else {
      try {
        const urlParams = extractUrlParams();
        urlParams.promoCode && storage.setKey(`promoCode`, urlParams.promoCode);
        const res = await login({
          email,
          otp,
        });
        if (res) {
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
  };

  const onForgotPassword = async () => {
    const res = await forgotPasswordandReset({
      email,
    });
    if (res) {
      showNotification({
        type: 'success',
        title: t('Please check your email for resetting your password.'),
      });
      setEmail('');
      setForgotPassword(false);
    }
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (forgotPassword) {
        await onForgotPassword();
      } else if (otpScreenData && !isSignUp) {
        await onVerifyOtp();
      } else if (!otpScreenData && !isSignUp) {
        await onRegister();
      }
    }
  };

  return (
    <div className="email-login-form" onKeyDown={handleKeyDown} role="presentation">
      {!isSignUp ? (
        <section className="consumer-login">
          {otpScreenData ? (
            <div className="registration-container">
              <BackIcon
                onClick={() => {
                  if (otpScreenData) {
                    setEnterOtpScreen(null);
                  } else {
                    onBack();
                  }
                }}
                className="back-svg"
              />
              <div className="logo-section">
                <Logo /> <p className="petsked-desc h2">Verify Email </p>
                <p className="petsked-desc">
                  Enter the 6-digit code sent to you at {otpScreenData?.email}
                </p>
              </div>

              <div className="otp-input-container">
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  autoFocus
                  OTPLength={6}
                  otpType="number"
                  className="otp-input-root"
                  inputClassName="otp-input"
                  disabled={false}
                  secure
                />
              </div>

              <div className="logo-section mt-4">
                {useOTPLoginMutation.isLoading ? (
                  <>
                    <span className="petsked-desc pink">Sending...</span>
                  </>
                ) : (
                  <a
                    className="petsked-desc pink"
                    onClick={async () => {
                      await useOTPLoginMutation.mutateAsync({
                        email: otpScreenData?.email,
                      });
                    }}
                  >
                    Resend code
                  </a>
                )}
              </div>
              <div className="petsked-form">
                <div className="action">
                  <button
                    className="petsked-button button-dark"
                    onClick={onVerifyOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader text="Signing up..." /> : <span>{'Sign In'}</span>}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="registration-container">
              {/* <div className='back-icon-div'> */}
              <BackIcon
                onClick={() => {
                  if (forgotPassword || isSignUp) {
                    setForgotPassword(false);
                    setIsSignup(false);
                  } else {
                    onBack();
                  }
                }}
                className="back-svg"
              />
              {/* </div> */}
              <div className="logo-section">
                <Logo />
                {forgotPassword ? (
                  <p className="petsked-desc h2">{t('Forgot Password')} </p>
                ) : (
                  <p className="petsked-desc">
                    {t('Get started,')}{' '}
                    {isSignUp ? t('create a new account') : t('login to your account')}
                  </p>
                )}
                {forgotPassword && (
                  <p className="petsked-desc">
                    {t('We’ll send you a reset password link to your email address')}
                  </p>
                )}
              </div>
              {forgotPassword ? (
                <div className="petsked-form">
                  <input
                    id="customer-mail"
                    type="email"
                    value={email}
                    className="cust-input-mobile"
                    onChange={handleChange}
                    placeholder={t('Enter your email')}
                    autoComplete="off"
                  />
                  <div className="action">
                    <button
                      className="petsked-button button-dark"
                      onClick={onForgotPassword}
                      disabled={isLoading}
                    >
                      <span>{t('Continue')}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="petsked-form">
                  <input
                    id="customer-mail"
                    type="email"
                    value={userData.email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleFormChange('email', e.target.value)
                    }
                    placeholder={t('Enter your email')}
                    autoComplete="off"
                  />

                  {loginType === 'password' && (
                    <span className="form-field-wrapper">
                      <input
                        id="customer-secret"
                        type={showPassword ? 'text' : 'password'}
                        value={userData.password}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleFormChange('password', e.target.value)
                        }
                        placeholder={t('Password')}
                        autoComplete="off"
                      />
                      {showPassword ? (
                        <PasswordHideIcon onClick={() => setShowPassword(!showPassword)} />
                      ) : (
                        <PasswordShowIcon onClick={() => setShowPassword(!showPassword)} />
                      )}
                    </span>
                  )}

                  <div className="action">
                    {!isSignUp && loginType === 'password' && (
                      <p className={`forgot-password-link`}>
                        <a
                          onClick={() => {
                            setForgotPassword(true);
                          }}
                        >
                          {t('Forgot password?')}
                        </a>
                      </p>
                    )}
                    <button
                      className="petsked-button button-dark"
                      onClick={onRegister}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader text="Signing up..." />
                      ) : (
                        <span>
                          {isSignUp ? 'Sign Up' : loginType === 'otp' ? 'Continue' : 'Sign In'}
                        </span>
                      )}
                    </button>

                    {!isSignUp && (
                      <>
                        <div className="action ">
                          <div className="or-div">OR</div>
                          <p className={`forgot-password-link otp-screen`}>
                            <button
                              className="petsked-button button-light otp-btn"
                              onClick={() => {
                                loginType === 'otp'
                                  ? setLoginType('password')
                                  : setLoginType('otp');
                              }}
                              disabled={isLoading}
                            >
                              Login with {loginType === 'otp' ? 'Password' : 'OTP'}{' '}
                            </button>
                          </p>
                        </div>
                      </>
                    )}
                    {isSignUp && (
                      <p className="terms">
                        {t('By continuing, you agree to our')} <b>{t('Terms')}</b> {t('and')}{' '}
                        <b>{t('Privacy Policy')}</b>, {t('and to receive emails and updates.')}
                      </p>
                    )}
                    <p className={`sign-up-link ${isSignUp ? 'signup' : ''}`}>
                      {!isSignUp && t(`Don't`)} {t('Have an account?')}{' '}
                      <a
                        onClick={() => {
                          if (isSignUp) {
                            setIsSignup(false);
                          } else {
                            setIsSignup(true);
                          }
                        }}
                      >
                        {isSignUp ? t('Sign In Now') : t('Sign Up Now')}
                      </a>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      ) : (
        <section>
          <OTPSignupFlow
            onBack={() => {
              setIsSignup(false);
            }}
            onLoginSuccess={() => {}}
          />
        </section>
      )}
    </div>
  );
};
